import { ButtonLink } from "@/components/ui/button-link";
import { FooterButtonLink } from "@/components/ui/footer-button-link";
// Ícones: Zap (WhatsApp), TrendingUp (Lucro), DollarSign, etc.
import { Instagram, Youtube, Play, Zap, CheckCircle, Lightbulb, Shield, Users, Award, Star, TrendingUp, DollarSign } from "lucide-react";
import { useState } from 'react';

// Import local video from src/videos/
import farmVideo from "../videos/farm.mp4";

// --- Configurações da Landing Page ---
const WHATSAPP_LINK = "https://wa.me/5521995251264?text=Ol%C3%A1%2C%20gostaria%20de%20garantir%20o%20desconto%20exclusivo%20de%20assinatura%20do%20Bitcampo%20App%20ainda%20hoje.";
const PLAY_STORE_LINK = "https://play.google.com/store/apps/details?id=com.willrpg.bitcampo";

// URLs de Mídia (YouTube e TikTok) com títulos aprimorados e CTA
const MEDIA_DEMOS = [
  {
    title: "Dica Rápida: Gestão de ABELHAS JATAÍ no Bitcampo! 🐝",
    url: "https://www.youtube.com/embed/yFbcRA5OTvM", // URL de Embed do YouTube Short
    description: "Veja como apicultores usam o app para monitorar colmeias, registrar tarefas e otimizar a produção de mel.",
    type: "youtube",
  },
  {
    title: "💰 Agro Finanças Simplificadas: Módulo Financeiro! (Guia Rápido)",
    url: "https://www.youtube.com/embed/Y9yfgCm1h3o", // URL de Embed do YouTube Short
    description: "Controle, planeje e ganhe clareza financeira para crescer com segurança no campo, tudo no seu celular.",
    type: "youtube",
  },
  {
    title: "🔥 Produtor Rural na Prática: 'O Bitcampo Facilitou Minha Vida!'",
    url: "https://www.tiktok.com/@agroraizverde/video/7567122627748318482", // URL Direta do TikTok
    description: "Depoimento real de como a tecnologia organiza manejo, tarefas e acompanhamento de animais. Clique para assistir no TikTok!",
    type: "tiktok"
  }
];

// --- Dados Estruturados OTIMIZADOS ---
const features = [
  {
    title: "Visualização Detalhada: Modelos 3D de Animais e Plantas",
    description: "Monitore a saúde e o desenvolvimento de rebanhos e culturas com modelos 3D interativos e detalhados. Veja cada animal e planta do seu inventário de forma imersiva e tome decisões de manejo mais precisas.",
    icon: <Lightbulb className="w-8 h-8 text-brand-signal-success" />
  },
  {
    title: "Gestão de Rebanhos e Pecuária Inteligente",
    description: "Obtenha controle completo sobre a saúde, alimentação e histórico do seu rebanho. Tome decisões baseadas em dados hoje mesmo e maximize seu lucro pecuário.",
    icon: <Users className="w-8 h-8 text-brand-signal-success" />
  },
  {
    title: "Controle Operacional de Culturas e Tarefas",
    description: "Registre e monitore todas as atividades agrícolas, do plantio à colheita. Garanta a execução eficiente das tarefas e assuma o controle total da sua safra.",
    icon: <CheckCircle className="w-8 h-8 text-brand-signal-success" />
  },
  {
    title: "Análise de Resultados e Produtividade Offline",
    description: "Visualize relatórios e gráficos financeiros robustos para análises estratégicas. Baixe o BitCampo e gerencie suas operações com segurança, mesmo em áreas sem internet.",
    icon: <Award className="w-8 h-8 text-brand-signal-success" />
  },
];

const testimonials = [
  {
    name: "João Silva",
    role: "Pecuarista em Minas Gerais",
    feedback: "O Bitcampo revolucionou a forma como eu gerencio meu gado. Tenho tudo na palma da mão e consigo tomar decisões muito mais rápido.",
    stars: 5,
  },
  {
    name: "Maria Oliveira",
    role: "Produtora Rural em São Paulo",
    feedback: "Simplesmente indispensável para o controle das minhas lavouras. O acompanhamento da safra nunca foi tão fácil e eficiente.",
    stars: 4,
  },
  {
    name: "Carlos Pereira",
    role: "Administrador de Fazenda no Mato Grosso",
    feedback: "A parte financeira é fantástica! Consigo ver exatamente para onde meu dinheiro está indo e onde posso otimizar. Recomendo muito!",
    stars: 5,
  },
];

const faqs = [
  {
    question: "O que é o Bitcampo?",
    answer: "O Bitcampo é um aplicativo de gestão rural completo, desenvolvido para auxiliar produtores e administradores a gerenciar rebanhos, culturas, finanças e muito mais, tudo em um só lugar."
  },
  {
    question: "Como posso assinar o Bitcampo?",
    answer: "Você pode clicar no botão 'Assinar via WhatsApp' em nossa página e falar diretamente com nossa equipe para conhecer os planos e adquirir sua assinatura. Estamos prontos para te ajudar!"
  },
  {
    question: "O Bitcampo funciona offline?",
    answer: "Alguns recursos podem exigir conexão com a internet para sincronização e atualizações, mas estamos trabalhando para expandir a funcionalidade offline sempre que possível."
  },
  {
    question: "Existe um período de teste gratuito?",
    answer: "Entre em contato conosco via WhatsApp para discutir opções e saber mais sobre demonstrações e ofertas especiais para novos usuários."
  }
];

// Componente para exibir estrelas
const StarRating = ({ count }) => {
  return (
    <div className="flex gap-1 text-brand-signal-alert" aria-label={`Avaliação de ${count} estrelas`}>
      {[...Array(count)].map((_, i) => (
        <Star key={i} className="w-5 h-5 fill-current" />
      ))}
    </div>
  );
};

// Componente de Item do FAQ
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-brand-shape-300 py-4">
      <button
        aria-expanded={isOpen}
        className="flex justify-between items-center w-full text-left font-semibold text-lg text-brand-primary-100 hover:text-brand-signal-success"
        onClick={() => setIsOpen(!isOpen)}
      >
        {question}
        <span className="text-xl">{isOpen ? '-' : '+'}</span>
      </button>
      {isOpen && (
        <p className="mt-2 text-brand-primary-50 leading-relaxed">
          {answer}
        </p>
      )}
    </div>
  );
};

const Index = () => {
  return (
    <div className="min-h-screen w-full bg-brand-shape-50 text-brand-primary-100 overflow-x-hidden">

      {/* SEO: Main Section - Hero */}
      <header className="relative w-full min-h-screen overflow-hidden flex items-center py-20 md:py-0">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src={farmVideo} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-brand-primary-100/70 backdrop-blur-[1px]" />
        <div className="relative mx-auto w-full max-w-7xl px-4 flex flex-col md:flex-row items-center justify-between gap-10 md:gap-20">
          <div className="text-center md:text-left md:w-3/5 flex flex-col justify-center animate-fade-in-left">
            <div className="flex items-center justify-center md:justify-start gap-4 mb-4">
              {/* <img
                src="/bitcampo-uploads/a8953663-c876-4f2f-9772-addb6844fbcf.png"
                alt="Logo do Aplicativo Bitcampo, gestão rural inteligente"
                className="h-14 w-14 rounded-xl bg-brand-signal-success p-2 shadow-xl"
              /> */}
              <h1 className="text-6xl md:text-8xl font-black text-brand-shape-50 leading-none tracking-tight drop-shadow-lg">
                Bitcampo - Gestão Rural
              </h1>
            </div>
            {/* CORREÇÃO: Negrito feito com span e font-bold */}
            <p className="text-3xl md:text-4xl text-brand-shape-100 mb-4 max-w-xl mx-auto md:mx-0 font-light drop-shadow-md">
              <span className="font-bold">Pare de perder dinheiro na fazenda!</span> Domine sua Safra e Rebanho com a tecnologia que garante <span className="font-bold">lucro imediato.</span>
            </p>
            <p className="text-xl md:text-2xl text-brand-signal-success font-semibold mb-8 max-w-xl mx-auto md:mx-0 drop-shadow-md">
              💡 <span className="font-bold">Modelos 3D:</span> Visualize cada animal e planta do seu inventário com detalhes imersivos.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start mb-12 animate-fade-in-up">
              <ButtonLink
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                size="lg"
                className="flex items-center gap-2 bg-brand-signal-success hover:bg-brand-primary-50 text-brand-shape-50 font-black text-xl px-10 py-5 rounded-full shadow-2xl transform transition-all duration-300 hover:scale-105 hover:ring-4 ring-brand-signal-success/50"
                aria-label="Assinar via WhatsApp e falar com nossa equipe para garantir oferta."
              >
                <Zap className="w-6 h-6" />Quero Desconto!
              </ButtonLink>
              {/* CTA SECUNDÁRIO: "COMECE AGORA" */}
              <ButtonLink
                href={WHATSAPP_LINK}
                size="lg"
                variant="outline"
                className="flex items-center gap-2 text-brand-shape-50 border-brand-shape-50 hover:bg-brand-shape-50/20 font-bold text-lg px-8 py-4 rounded-full shadow-lg transform transition-transform duration-300 hover:scale-105"
                aria-label="Comece Agora sua Assinatura do Bitcampo."
              >
                <Play className="w-6 h-6" /> Comece Agora
              </ButtonLink>
            </div>
          </div>

          <div className="w-full md:w-2/5 flex justify-center mt-8 md:mt-0 animate-fade-in-right">
            <a
              href={PLAY_STORE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer"
              aria-label="Visualizar screenshot do aplicativo Bitcampo na Play Store."
            >
              <img
                src="/bitcampo-uploads/1a963856-990f-4ec2-8ac9-0344602a5f61.png"
                alt="Interface do Bitcampo mostrando o controle de mapas e Modelos 3D para gestão de fazenda"
                className="w-[250px] sm:w-[280px] rounded-[2.5rem] shadow-2xl ring-4 ring-brand-signal-success/50 transition-all duration-700
                rotate-3
                hover:scale-105
                hover:shadow-3xl
                hover:-translate-y-3
                hover:rotate-1
                hover:brightness-110"
              />
            </a>
          </div>
        </div>
      </header>

      {/* MARKETING: Seção de Convencimento (Estilo Netflix) */}
      <section id="preco-assinatura" className="py-16 bg-brand-primary-100 text-center text-brand-shape-50 shadow-inner" role="region" aria-label="Preços e Assinatura do Bitcampo">
        <div className="mx-auto w-full max-w-7xl px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-extrabold text-brand-shape-50 mb-6">
              Gestão rural completa e organizada, sem limites.
            </h2>
            <p className="text-2xl md:text-3xl font-bold text-brand-signal-success mb-6">
              A partir de R$ 49,90/mês. Cancele quando quiser.
            </p>
            <p className="text-xl md:text-2xl text-brand-shape-100 mb-10">
              Pronto para transformar sua fazenda em um negócio mais lucrativo?
            </p>

            <ButtonLink
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              size="lg"
              className="flex items-center justify-center mx-auto gap-2 bg-brand-signal-success hover:bg-brand-primary-50 text-brand-shape-50 font-black text-xl px-10 py-5 rounded-full shadow-2xl transform transition-all duration-300 hover:scale-105 hover:ring-4 ring-brand-signal-success/50"
              aria-label="Quero Desconto Exclusivo e Assinar o Bitcampo."
            >
              <Zap className="w-6 h-6" />Quero Desconto!
            </ButtonLink>

            <p className="text-sm mt-4 text-brand-shape-100 opacity-80">
              *Fale com um nossa equipe para garantir a tarifa promocional.
            </p>
          </div>
        </div>
      </section>

      {/* NOVA SEÇÃO: Vídeos de Demonstração (Prova de Funcionamento) */}
      <section id="demonstracao" className="py-16 md:py-24 bg-brand-shape-50" role="region" aria-labelledby="videos-titulo">
        <div className="mx-auto w-full max-w-7xl px-4 text-center">
          <h2 id="videos-titulo" className="text-4xl md:text-5xl font-extrabold text-brand-primary-100 mb-6">
            Veja o Bitcampo em Ação!
          </h2>
          <p className="text-xl md:text-2xl text-brand-primary-50 mb-12 max-w-3xl mx-auto">
            Assista aos tutoriais e depoimentos para descobrir como é fácil otimizar sua gestão rural.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {MEDIA_DEMOS.map((media, index) => (
              <div key={index} className="bg-brand-shape-100 p-4 rounded-xl shadow-xl">
                {media.type === 'youtube' ? (
                  <div className="relative aspect-video mb-4 rounded-lg overflow-hidden">
                    {/* Embed responsivo do YouTube */}
                    <iframe
                      width="100%"
                      height="100%"
                      src={media.url}
                      title={media.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                    ></iframe>
                  </div>
                ) : (
                  // Card Clicável para o TikTok
                  <a
                    href={media.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block relative aspect-video mb-4 rounded-lg overflow-hidden bg-brand-primary-300 flex items-center justify-center group transition-opacity hover:opacity-80"
                    aria-label={`Assistir ao vídeo: ${media.title} no TikTok`}
                  >
                    <Users className="w-16 h-16 text-brand-signal-success group-hover:scale-110 transition-transform" />
                    <p className="absolute bottom-2 left-2 text-sm text-brand-shape-50 font-bold bg-black/70 px-2 py-1 rounded">
                      VER NO TIKTOK
                    </p>
                  </a>
                )}
                <h3 className="text-lg font-bold text-brand-primary-60 mb-1">{media.title}</h3>
                <p className="text-sm text-brand-primary-50">{media.description}</p>
              </div>
            ))}
          </div>
          {/* CTA para o canal do YouTube */}
          <ButtonLink
            href="https://www.youtube.com/@bitcampo"
            target="_blank"
            variant="ghost"
            className="mt-12 text-lg text-brand-primary-100 border border-brand-primary-100 hover:bg-brand-primary-100 hover:text-brand-shape-50 font-bold py-3 px-6 rounded-full transition-colors"
            aria-label="Ver mais vídeos no nosso canal do YouTube"
          >
            <Youtube className="w-5 h-5 mr-2" /> Ver mais tutoriais no YouTube
          </ButtonLink>
        </div>
      </section>

      {/* SEO: Section for Features (Detalhe os benefícios H2) */}
      <section id="recursos" className="py-16 md:py-24 bg-brand-shape-100" role="region" aria-labelledby="recursos-titulo-2">
        <div className="mx-auto w-full max-w-7xl px-4 text-center">
          <h2 id="recursos-titulo-2" className="text-4xl md:text-5xl font-extrabold text-brand-primary-100 mb-6">
            Recursos Inteligentes para sua Fazenda
          </h2>
          <p className="text-xl md:text-2xl text-brand-primary-50 mb-12 max-w-3xl mx-auto">
            Oferecemos ferramentas que eliminam a incerteza e transformam dados em decisões rentáveis para o seu agronegócio.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-brand-shape-50 p-8 rounded-2xl shadow-xl border-t-4 border-brand-signal-success flex flex-col items-center transform transition-transform duration-300 hover:scale-[1.03] hover:shadow-2xl"
              >
                <div className="bg-brand-shape-200 p-4 rounded-full mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-2xl font-bold text-brand-primary-60 mb-3">
                  {feature.title}
                </h3>
                <p className="text-brand-primary-50 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARKETING: CTA Forte Intermediário (Gatilho de Ação) */}
      <section className="bg-brand-primary-300 py-16 md:py-20 text-center text-brand-shape-50" role="region" aria-labelledby="cta-titulo">
        <div className="mx-auto w-full max-w-7xl px-4">
          <h2 id="cta-titulo" className="text-4xl md:text-5xl font-black mb-6">
            Essa Oferta de R$ 49,90 é Por Tempo Limitado!
          </h2>
          <p className="text-xl md:text-2xl font-light max-w-3xl mx-auto mb-10">
            Fale agora com nossa equipe e garanta sua assinatura com o <span className="font-bold">preço exclusivo de lançamento.</span>
          </p>
          <ButtonLink
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            size="lg"
            className="flex items-center justify-center gap-3 bg-brand-signal-success hover:bg-brand-primary-50 text-brand-shape-50 font-extrabold text-xl px-10 py-5 rounded-full shadow-2xl transform transition-transform duration-300 hover:scale-105"
            aria-label="Quero garantir minha assinatura com o preço exclusivo de lançamento!"
          >
            <Zap className="w-7 h-7" />Quero Assinar AGORA!
          </ButtonLink>
        </div>
      </section>

      {/* SEO: Section for Testimonials (Prova Social com H2) */}
      <section id="depoimentos" className="py-16 md:py-24 bg-brand-shape-50" role="region" aria-labelledby="depoimentos-titulo">
        <div className="mx-auto w-full max-w-7xl px-4 text-center">
          <h2 id="depoimentos-titulo" className="text-4xl md:text-5xl font-extrabold text-brand-primary-100 mb-6">
            A Prova Social que Você Precisa
          </h2>
          <p className="text-xl md:text-2xl text-brand-primary-50 max-w-3xl mx-auto mb-12">
            Veja como o Bitcampo transformou fazendas em todo o Brasil.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-brand-shape-100 p-8 rounded-2xl shadow-xl border-b-4 border-brand-signal-success/50 flex flex-col items-center text-center transform transition-transform duration-300 hover:scale-[1.03] hover:shadow-2xl"
                role="blockquote"
              >
                <StarRating count={testimonial.stars} />
                <p className="text-brand-primary-50 italic mt-4 mb-6 text-lg leading-relaxed">
                  "{testimonial.feedback}"
                </p>
                <p className="font-bold text-brand-primary-60 text-lg">
                  {testimonial.name}
                </p>
                <p className="text-brand-primary-50 text-sm">
                  {testimonial.role}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO: Section for FAQ (Perguntas e Respostas) */}
      <section id="faq" className="py-16 md:py-24 bg-brand-shape-100" role="region" aria-labelledby="faq-titulo">
        <div className="mx-auto w-full max-w-7xl px-4">
          <h2 id="faq-titulo" className="text-4xl md:text-5xl font-extrabold text-center text-brand-primary-100 mb-12">
            Perguntas Frequentes (FAQ)
          </h2>
          <div className="max-w-4xl mx-auto">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* SEO: Navigation/Social Media Section */}
      <nav className="w-full bg-brand-primary-100 text-brand-shape-50 py-16" aria-label="Links de Contato e Redes Sociais">
        <div className="mx-auto w-full max-w-7xl px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Fale Conosco Hoje
          </h2>
          <div className="flex flex-wrap justify-center gap-6 md:gap-8">
            <FooterButtonLink
              href="https://www.instagram.com/bitcampo_app/"
              variant="ghost"
              icon={<Instagram className="w-7 h-7" />}
              className="text-brand-shape-50 hover:bg-brand-signal-success/50 p-4 rounded-full text-lg"
              aria-label="Instagram do Bitcampo"
            >
              Instagram
            </FooterButtonLink>
            <FooterButtonLink
              href="https://www.youtube.com/@bitcampo"
              variant="ghost"
              icon={<Youtube className="w-7 h-7" />}
              className="text-brand-shape-50 hover:bg-brand-signal-success/50 p-4 rounded-full text-lg"
              aria-label="Canal do YouTube do Bitcampo"
            >
              YouTube
            </FooterButtonLink>
            <FooterButtonLink
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              variant="ghost"
              icon={<Zap className="w-7 h-7" />}
              className="text-brand-signal-success hover:bg-brand-signal-success/30 p-4 rounded-full text-lg font-bold"
              aria-label="Falar no WhatsApp para Assinatura Exclusiva"
            >
              WhatsApp Exclusivo
            </FooterButtonLink>
          </div>
        </div>
      </nav>

      {/* SEO: Footer Section (Copyright e Informações) */}
      <footer className="w-full bg-brand-primary-60 text-brand-shape-50 py-8" role="contentinfo">
        <div className="mx-auto w-full max-w-7xl px-4 text-center">
          <p className="text-base">
            © {new Date().getFullYear()} Bitcampo. Todos os direitos reservados.
          </p>
          {/* CNPJ REMOVIDO */}
          <p className="text-sm mt-2 opacity-80">
            Feito com dedicação para o agronegócio brasileiro.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;